import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useLogin } from 'contexts';
import { trackUserLoggedIn, trackClickedButton, trackFormSubmitted } from './metrics';

const IGNORED_PATHS = [/^\/googleoauth/i, /^\/oauth/i, /^\/auth(\/callback)?/i, /^\/login(-external)?$/i];
const ROLES_KEY = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

export const PostHogListener = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const { user } = useLogin();

  const lastPathRef = useRef<string | null>(null);
  const lastUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;
    const currentPath = location.pathname;

    if (lastPathRef.current === currentPath + location.search) return;
    if (IGNORED_PATHS.some((rx) => rx.test(currentPath))) return;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: currentPath,
    });

    lastPathRef.current = currentPath + location.search;
  }, [location, posthog]);

  useEffect(() => {
    if (!posthog) return;

    if (user?.user_id) {
      if (lastUserRef.current === String(user.user_id)) return;

      const isInternal = user.email?.includes('@popronsystems.cz') || user.email?.includes('@veolia.com');

      posthog.identify(user.user_id, {
        email: user.email,
        name: user.full_name,
        is_internal: isInternal,
        roles: user[ROLES_KEY] || [],
        tenant_id: user.tenant_id,
      });

      if (user.tenant_id) {
        posthog.group('company', user.tenant_id, { id: user.tenant_id });
      }

      trackUserLoggedIn({
        user_id: user.user_id,
        email: user.email,
        name: user.full_name,
        process: 'auth',
        step: 'login_ok',
      });

      posthog.reloadFeatureFlags();
      lastUserRef.current = String(user.user_id);
    } else if (!user && lastUserRef.current) {
      posthog.reset();
      posthog.reloadFeatureFlags();
      lastUserRef.current = null;
      lastPathRef.current = null;
    }
  }, [user, posthog]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const btn = target.closest('button, [role="button"]') as HTMLElement | null;
      if (!btn) return;

      const explicit = btn.getAttribute('data-ph-button') || btn.getAttribute('ph-button');
      const label = explicit || btn.getAttribute('aria-label') || (btn.textContent || '').trim() || btn.getAttribute('name') || 'button';

      try {
        trackClickedButton({ button: label, process: 'ui' });
      } catch {
        /* noop */
      }
    };

    const onSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement | null;
      if (!form) return;

      const explicit = form.getAttribute('data-ph-form') || form.getAttribute('ph-form');
      const name = explicit || form.getAttribute('name') || form.getAttribute('id') || 'form';

      try {
        trackFormSubmitted({ form: name, process: 'ui' });
      } catch {
        /* noop */
      }
    };

    window.addEventListener('click', onClick, true);
    window.addEventListener('submit', onSubmit, true);

    return () => {
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('submit', onSubmit, true);
    };
  }, []);

  return null;
};
